'use client';

import React, { CSSProperties } from 'react';
import classNames from 'classnames';
import AnchorLink from '@/components/TableOfContents/AnchorLink';
import Button from '@/components/Button/Button';
import BrandLogo from '@/components/BrandLogo/BrandLogo';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import AnimatedLink from '@/components/AnimatedLink/AnimatedLink';
import { analytics } from '@/utils/analytics';
import { composeImageUrl } from '@/utils/imageAsset';
import { bgColorMap } from '@/utils/styleHelpers';
import { BrandType, DescriptionType, ImageType } from '@/utils/common.types';

interface CardProps {
  brand?: BrandType;
  title: string;
}

const getCardTitle = ({ brand, title }: CardProps) => {
  return brand ? (
    <div className="m-0 p-0 font-acumin text-[1.65rem] font-bold leading-[1.2] tracking-[-0.35px] text-hbg-blue lg:text-[2rem]">
      <BrandLogo brand={brand} />
    </div>
  ) : (
    title && (
      <p className="m-0 p-0 font-acumin text-[1.65rem] font-bold leading-[1.2] tracking-[-0.35px] text-hbg-blue lg:text-[2rem]">
        {title}
      </p>
    )
  );
};

interface Props {
  alignment: string;
  anchorTagRef?: string;
  brand?: BrandType;
  buttonLabel: string;
  buttonOpenNewWindow: boolean;
  buttonUrl: string;
  buttonStyle: string;
  description: DescriptionType;
  image: {
    desktop?: ImageType;
    mobile?: ImageType;
  };
  includeCardShadow?: boolean;
  includeSegment?: boolean;
  backgroundColor?: string;
  backgroundColorTw?: string;
  title: string;
}

const FeatureCard = ({
  title,
  description,
  image,
  alignment,
  buttonLabel,
  buttonUrl,
  buttonStyle,
  buttonOpenNewWindow,
  brand,
  anchorTagRef,
  includeCardShadow,
  includeSegment,
  backgroundColorTw,
}: Props) => {
  const isImageCircle = image?.desktop?.style?.isCircle;
  const isMaxWidth = !isImageCircle && image?.desktop?.style?.isMaxWidth;
  const setAsBackgroundImage = !isImageCircle && !isMaxWidth;

  return (
    <section
      id="feature-card"
      className={classNames(
        'mx-auto my-10 w-mobile md:max-w-standard md:scroll-mt-[5rem]',
        {
          [bgColorMap(backgroundColorTw)]: backgroundColorTw,
          ['shadow-component']: includeCardShadow,
        }
      )}
    >
      {anchorTagRef && <AnchorLink anchorTagRef={anchorTagRef} />}
      <section
        className={classNames(
          'grid grid-cols-[auto] grid-rows-[auto] md:scroll-mt-[5rem] md:gap-x-0',
          {
            ['md:grid-cols-[1fr_1.2fr]']: alignment === 'left',
            ['md:grid-cols-[1.2fr_1fr]']: alignment !== 'left',
          }
        )}
        id={anchorTagRef && `anchortag-${anchorTagRef}`}
      >
        <div
          className={classNames(
            'col-start-1 row-start-1 flex flex-col-reverse self-center',
            { ['md:col-start-2']: alignment !== 'left' }
          )}
        >
          <span className="inline w-full px-[30px] pt-4 md:hidden lgMobile:px-[34px] lgMobile:pt-5">
            {getCardTitle({ brand, title })}
          </span>
          <div
            className={classNames(
              'relative mx-auto my-0 flex h-full w-full items-center overflow-hidden bg-cover bg-[50%_0%] bg-no-repeat px-0 md:min-h-[520px]',
              {
                [bgColorMap(backgroundColorTw)]: backgroundColorTw,
                ['bg-[image:var(--mobileImage-url)] pt-[100%] lg:bg-[image:var(--image-url)]']:
                  setAsBackgroundImage,
              }
            )}
            style={
              {
                '--mobileImage-url':
                  setAsBackgroundImage &&
                  `url('${composeImageUrl({ image: image?.mobile, h: isMaxWidth ? 0 : 550, w: 550 })}')`,
                '--image-url':
                  setAsBackgroundImage &&
                  `url('${composeImageUrl({ image: image?.desktop, h: 550, w: 550 })}`,
              } as CSSProperties
            }
          >
            {image?.desktop?.url && !setAsBackgroundImage && (
              <picture className="mx-auto my-0 w-full">
                <source
                  srcSet={composeImageUrl({
                    image: image?.desktop,
                    w: 550,
                    h: isMaxWidth ? 0 : 550,
                  })}
                  media="(min-width: 768px)"
                  content={image?.desktop?.contentType}
                />
                <img
                  src={composeImageUrl({
                    image: image?.mobile,
                    w: 550,
                    h: isMaxWidth ? 0 : 550,
                  })}
                  alt={image?.mobile?.title}
                  content={image?.mobile?.contentType}
                  className={classNames('w-full', {
                    ['h-full w-auto']: isImageCircle || isMaxWidth,
                    ['md:pl-7 md:pr-0']:
                      (isMaxWidth || isImageCircle) && alignment === 'left',
                    ['md:pl-0 md:pr-7']:
                      (isMaxWidth || isImageCircle) && alignment !== 'left',
                    ['px-3 pt-3 md:px-0 md:pt-0']: isImageCircle,
                  })}
                />
              </picture>
            )}
          </div>
        </div>
        <div
          className={classNames(
            'col-start-1 row-start-2 self-center px-[34px] pb-5 pt-0 md:row-start-1 md:my-5',
            { ['md:col-start-2']: alignment === 'left' }
          )}
        >
          <span className="hidden w-full pt-10 md:inline">
            {getCardTitle({ brand, title })}
          </span>
          <div
            className={classNames(
              'px-0 pb-5 pt-[27px] font-source-sans text-[1rem] leading-[27px] text-hbg-blue md:pt-[27px] lg:text-[1.15rem]',
              'split-content'
            )}
          >
            {description && ContentfulRichText(description)}
          </div>
          {buttonUrl &&
            (buttonStyle === 'arrow' ? (
              <AnimatedLink
                title={buttonLabel}
                buttonUrl={buttonUrl}
                buttonOpenNewWindow={buttonOpenNewWindow}
                onClick={() =>
                  includeSegment &&
                  analytics?.track('CTA Clicked', {
                    isNav: true,
                    type: 'text',
                    text: buttonLabel,
                  })
                }
              />
            ) : (
              <Button
                label={buttonLabel}
                href={buttonUrl}
                openNewWindow={buttonOpenNewWindow}
                onClick={() =>
                  includeSegment &&
                  analytics?.track('CTA Clicked', {
                    isNav: true,
                    type: 'button',
                    text: buttonLabel,
                  })
                }
              />
            ))}
        </div>
      </section>
    </section>
  );
};
export default FeatureCard;
